import { dateRawMedium } from "@/services/filters";

/**
 * Bulk discount display and matching.
 *
 * A discount is a schedule over groups, each a complete rule: the products it targets, what
 * triggers it and the amount or percent off. A group targets at most one category, type, brand and
 * product, all of which must match (an unset one means "any"); groups are alternatives. A unit
 * minimum also fixes the unit the products are listed in and how they are sold; a spend minimum
 * covers every unit, and with no targets the whole order. Whether a minimum is met is decided in
 * the cart.
 */

const bySpend = (group) => group.minimum_type == "amount";
const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const matchesOrAny = (targetId, id) => targetId == null || targetId == id;

function groupMatches(group, product) {
  return (
    (bySpend(group) || group.product_unit_measurement_id == product.product_unit_measurement_id) &&
    matchesOrAny(group.product_category_id, product.product_category_id ?? product.category_id) &&
    matchesOrAny(group.product_type_id, product.product_type_id ?? product.type_id) &&
    matchesOrAny(group.brand_id, product.brand_id) &&
    matchesOrAny(group.product_id, product.id)
  );
}

/** A product whose priced variant carries bulk_discounts_excluded (its winning pricing tier keeps bulk discounts off) gets none. */
export function bulkDiscountsExcluded(product) {
  for (const batch of product?.batches || []) {
    const variant = (batch.pricingVariants || []).find((v) => !v.is_sample);
    if (variant) return !!variant.bulk_discounts_excluded;
  }
  return false;
}

/** The discounts that can apply to a product, each narrowed to the groups that match it. */
export function bulkDiscountsForProduct(bulkDiscounts, product) {
  if (!product || bulkDiscountsExcluded(product)) return [];
  return (bulkDiscounts || [])
    .map((discount) => ({ ...discount, groups: (discount.groups || []).filter((group) => groupMatches(group, product)) }))
    .filter((discount) => discount.groups.length);
}

// Live Plant cases are sold as flats.
const soldAsLivePlant = (group) => group.product_category?.name == "Live Plant";

/** The unit a group counts and discounts in: its sold-as (Case) or its base unit of measurement. */
export function bulkDiscountUnit(group, plural = false) {
  const unit = group.package_case_type == "Case" ? (soldAsLivePlant(group) ? "Flat" : "Case") : group.unit_of_measurement?.name || "Unit";
  return plural ? `${unit}s` : unit;
}

/** "$1.00 off / case", "$50.00 off" (a spend group's flat amount comes off the total) or "10% off". */
export function bulkDiscountValue(group) {
  if (group.discount_type == "percent") return `${Number(group.percent)}% off`;
  return bySpend(group) ? `${dollars(group.discount)} off` : `${dollars(group.discount)} off / ${bulkDiscountUnit(group).toLowerCase()}`;
}

/** What triggers a group: "Order 10+ units" or "Spend $500.00+". */
export function bulkDiscountMinimum(group) {
  return bySpend(group)
    ? `Spend ${dollars(group.minimum_amount)}+`
    : `Order ${group.minimum_quantity}+ ${bulkDiscountUnit(group, true).toLowerCase()}`;
}

/** What a group targets, e.g. "Flower – Indica · Brand A"; a spend group with no targets is the entire order. */
export function bulkDiscountTargets(group) {
  const categoryAndType = [group.product_category?.short_display_name, group.product_type?.name].filter(Boolean).join(" – ");
  const targets = [categoryAndType, group.brand?.name, group.product?.name].filter(Boolean).join(" · ");
  return targets || (bySpend(group) ? "Entire order" : "Any product");
}

/** When a discount runs. The expiration date is the first day it no longer applies. */
export function bulkDiscountSchedule(discount) {
  if (discount.schedule_type == "monthly") {
    return `Days ${discount.month_start_day}–${discount.month_end_day} of every month`;
  }
  if (discount.schedule_type == "date_range") {
    const from = discount.starts_on ? `From ${dateRawMedium(discount.starts_on)}` : "";
    const until = discount.expiration ? `Expires ${dateRawMedium(discount.expiration)}` : "";
    return [from, until].filter(Boolean).join(" · ") || "Always";
  }
  return "Always";
}

const DEFAULT_UNIT_BY_CATEGORY = {
  Unit: ["Edibles & Drink", "Tincture", "Cartridge", "Live Plant", "Topicals & Wellness", "Prepack", "Preroll", "Merchandise", "Extract"],
  Pound: ["Plant Material", "Seed", "Flower"],
  Kilogram: ["Bulk Extract"],
  Gram: ["Pack To Order"],
};

/** The unit of measurement products in a category are usually listed in, to preselect for a new discount. */
export function defaultUnitNameForCategory(category) {
  return Object.keys(DEFAULT_UNIT_BY_CATEGORY).find((unit) => DEFAULT_UNIT_BY_CATEGORY[unit].includes(category?.name)) || null;
}

/*
 * Pricing tiers overrule bulk discounts: a line priced by a tier with exclude_from_bulk_discounts
 * gets none, and the tier that prices a line is whichever of the buyer's tiers gives the lowest
 * price. Which tier wins depends on each product's price, so a store-wide list can only drop a
 * group when the answer is certain: a tier that excludes bulk discounts prices everything the
 * group covers, and no other tier could price any of it.
 */

const tierActive = (tier) => !tier.expiration || new Date(tier.expiration) >= new Date();

// What every product in a group shares, from its own targets or those its type or product imply.
function groupScope(group) {
  return {
    categoryId: group.product_category_id ?? group.product_type?.product_category_id ?? group.product?.product_category_id ?? null,
    typeId: group.product_type_id ?? group.product?.product_type_id ?? null,
    brandId: group.brand_id ?? group.product?.brand_id ?? null,
    unitId: group.product_unit_measurement_id,
    soldAsCase: group.package_case_type == "Case",
    // A spend group covers every unit, singles and cases.
    anyUnit: bySpend(group),
  };
}

const priceSoldAs = (price, scope) => (scope.soldAsCase ? price.applies_to_cases : price.applies_to_singles);
const priceCoversSoldAs = (price, scope) => (scope.anyUnit ? price.applies_to_singles && price.applies_to_cases : priceSoldAs(price, scope));
const priceOverlapsSoldAs = (price, scope) => (scope.anyUnit ? price.applies_to_singles || price.applies_to_cases : priceSoldAs(price, scope));

// The tier price applies to every product in the group.
const priceCovers = (price, scope) =>
  price.product_category_id == scope.categoryId &&
  scope.categoryId != null &&
  (!price.product_type_id || price.product_type_id == scope.typeId) &&
  (!price.brand_id || price.brand_id == scope.brandId) &&
  (!price.product_unit_measurement_id || (!scope.anyUnit && price.product_unit_measurement_id == scope.unitId)) &&
  priceCoversSoldAs(price, scope);

// The tier price could apply to some product in the group.
const priceOverlaps = (price, scope) =>
  (scope.categoryId == null || price.product_category_id == scope.categoryId) &&
  (!price.product_type_id || scope.typeId == null || price.product_type_id == scope.typeId) &&
  (!price.brand_id || scope.brandId == null || price.brand_id == scope.brandId) &&
  (!price.product_unit_measurement_id || scope.anyUnit || price.product_unit_measurement_id == scope.unitId) &&
  priceOverlapsSoldAs(price, scope);

const tierPrices = (tier, scope, test) => tier.type == "Total" || (tier.pricing_tier_prices || []).some((price) => test(price, scope));

function groupOverruled(group, tiers) {
  const scope = groupScope(group);
  const excluding = tiers.filter((tier) => tier.exclude_from_bulk_discounts);
  const others = tiers.filter((tier) => !tier.exclude_from_bulk_discounts);
  return excluding.some((tier) => tierPrices(tier, scope, priceCovers)) && !others.some((tier) => tierPrices(tier, scope, priceOverlaps));
}

/** The bulk discounts a buyer can receive, narrowed to the groups their pricing tiers leave in play. */
export function bulkDiscountsForBuyer(bulkDiscounts, buyer) {
  if (!buyer) return bulkDiscounts || [];
  if (buyer.disable_bulk_discounts) return [];
  const tiers = (buyer.pricing_tiers || []).filter(tierActive);
  return (bulkDiscounts || [])
    .map((discount) => ({ ...discount, groups: (discount.groups || []).filter((group) => !groupOverruled(group, tiers)) }))
    .filter((discount) => discount.groups.length);
}
